import { Controller } from '@hotwired/stimulus';

const ZONE_CLASS = 'toolkit-image-drop-zone-component__zone';
const ACTIONS_VISIBLE_CLASS = 'toolkit-image-drop-zone-component__actions--visible';
// Mutually exclusive; --active and --error layer on top of these.
const BASE_STATES = ['empty', 'has-image', 'pending', 'uploading'] as const;
type BaseState = typeof BASE_STATES[number];

export default class extends Controller<HTMLElement> {
  static targets = ['zone', 'input', 'image', 'placeholder', 'caption', 'overlay', 'error', 'actions'];

  static values = {
    maxFileSize: Number,
    hasImage: Boolean,
    imageUrl: String,
    invalidTypeMessage: String,
    tooLargeMessage: String,
    networkMessage: String,
    replaceHint: String,
    pendingHint: String,
    dropToReplaceText: String,
    uploadingText: String
  };

  declare readonly zoneTarget: HTMLLabelElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly imageTarget: HTMLImageElement;
  declare readonly captionTarget: HTMLElement;
  declare readonly overlayTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly actionsTarget: HTMLElement;

  declare readonly maxFileSizeValue: number;
  declare readonly hasImageValue: boolean;
  declare readonly imageUrlValue: string;
  declare readonly invalidTypeMessageValue: string;
  declare readonly tooLargeMessageValue: string;
  declare readonly networkMessageValue: string;
  declare readonly replaceHintValue: string;
  declare readonly pendingHintValue: string;
  declare readonly dropToReplaceTextValue: string;
  declare readonly uploadingTextValue: string;

  private dragCounter = 0;
  private baseState: BaseState = 'empty';
  private pendingObjectUrl: string | null = null;
  private form: HTMLFormElement | null = null;

  private readonly onSubmitStart = () => this.startUploading();
  private readonly onFetchError = () => this.failUploading();

  connect() {
    this.baseState = this.hasImageValue ? 'has-image' : 'empty';
    this.form = this.element.closest('form');
    this.form?.addEventListener('turbo:submit-start', this.onSubmitStart);
    this.form?.addEventListener('turbo:fetch-request-error', this.onFetchError);
  }

  disconnect() {
    this.form?.removeEventListener('turbo:submit-start', this.onSubmitStart);
    this.form?.removeEventListener('turbo:fetch-request-error', this.onFetchError);
    this.reset();
  }

  dragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragCounter += 1;
    this.overlayTarget.textContent = this.dropToReplaceTextValue;
    this.setDragActive(true);
  }

  dragOver(event: DragEvent) {
    event.preventDefault();
  }

  dragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) this.setDragActive(false);
  }

  drop(event: DragEvent) {
    event.preventDefault();
    this.dragCounter = 0;
    this.setDragActive(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.inputTarget.files = files;
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  fileSelected() {
    this.clearError();

    const file = this.inputTarget.files?.[0];
    if (!file) return;
    if (!this.acceptFile(file)) return;

    this.preview(file);
  }

  reset() {
    this.inputTarget.value = '';
    this.inputTarget.disabled = false;

    if (this.pendingObjectUrl) {
      URL.revokeObjectURL(this.pendingObjectUrl);
      this.pendingObjectUrl = null;
    }

    this.clearError();
    this.actionsTarget.hidden = true;
    this.actionsTarget.classList.remove(ACTIONS_VISIBLE_CLASS);
    this.actionsTarget.querySelectorAll('button').forEach((button) => {
      (button as HTMLButtonElement).disabled = false;
    });
    this.element.removeAttribute('aria-busy');

    if (this.hasImageValue && this.imageUrlValue) {
      this.imageTarget.src = this.imageUrlValue;
      this.imageTarget.hidden = false;
      this.captionTarget.textContent = this.replaceHintValue;
      this.setState('has-image');
    } else {
      this.imageTarget.removeAttribute('src');
      this.imageTarget.hidden = true;
      this.captionTarget.textContent = '';
      this.setState('empty');
    }
  }

  private acceptFile(file: File): boolean {
    const accepted = this.inputTarget.accept.split(',').map((type) => type.trim()).filter(Boolean);
    if (accepted.length > 0 && accepted.indexOf(file.type) === -1) {
      this.rejectFile(this.invalidTypeMessageValue);
      return false;
    }

    if (this.maxFileSizeValue > 0 && file.size > this.maxFileSizeValue) {
      this.rejectFile(this.tooLargeMessageValue);
      return false;
    }

    return true;
  }

  private rejectFile(message: string) {
    this.inputTarget.value = '';
    this.showError(message);
  }

  private preview(file: File) {
    if (this.pendingObjectUrl) URL.revokeObjectURL(this.pendingObjectUrl);

    this.pendingObjectUrl = URL.createObjectURL(file);
    this.imageTarget.src = this.pendingObjectUrl;
    this.imageTarget.hidden = false;
    this.captionTarget.textContent = this.pendingHintValue;
    this.actionsTarget.hidden = false;
    this.actionsTarget.classList.add(ACTIONS_VISIBLE_CLASS);
    this.setState('pending');
  }

  private startUploading() {
    // Disable only after submit-start: Turbo has already serialized FormData by then.
    this.inputTarget.disabled = true;
    this.actionsTarget.querySelectorAll('button').forEach((button) => {
      (button as HTMLButtonElement).disabled = true;
    });
    this.overlayTarget.textContent = this.uploadingTextValue;
    this.element.setAttribute('aria-busy', 'true');
    this.setState('uploading');
  }

  private failUploading() {
    this.inputTarget.disabled = false;
    this.actionsTarget.querySelectorAll('button').forEach((button) => {
      (button as HTMLButtonElement).disabled = false;
    });
    this.element.removeAttribute('aria-busy');
    this.showError(this.networkMessageValue);
    this.setState('pending');
  }

  private showError(message: string) {
    this.errorTarget.textContent = message;
    this.zoneTarget.classList.add(`${ZONE_CLASS}--error`);
  }

  private clearError() {
    this.errorTarget.textContent = '';
    this.zoneTarget.classList.remove(`${ZONE_CLASS}--error`);
  }

  private setState(state: BaseState) {
    BASE_STATES.forEach((mod) => this.zoneTarget.classList.toggle(`${ZONE_CLASS}--${mod}`, mod === state));
    this.baseState = state;
    if (this.dragCounter === 0) this.element.setAttribute('data-state', state);
  }

  private setDragActive(active: boolean) {
    this.zoneTarget.classList.toggle(`${ZONE_CLASS}--active`, active);
    this.element.setAttribute('data-state', active ? 'active' : this.baseState);
  }
}
